/**
 * Rectangle multi-select wiring for the merged table's availability grid
 * (offer-unified-table, CU-86cbc3r8b): click/shift-click/drag rectangle
 * selection, cell resolution for the unified select popover, and narrowing
 * the selection after a batch reserve/release response. Extracted from
 * `use-offer-table-availability.tsx` — see that file's own doc comment.
 *
 * Delegates to `use-multi-select-click.ts` and `use-multi-select-drag.ts` —
 * each independently under the `.ts` max-lines-per-function budget — and to
 * `useSelectBatchResults` from `@ooh/screens`.
 */
import * as React from 'react';
import type { BatchReleaseResponse, BatchReserveResponse } from '@ooh/contracts';
import {
  type AvailabilityGridData,
  type MultiCellSelection,
  type MultiSelectCellRef,
  type InventoryAvailabilityPeriod,
  RICH_AVAILABILITY_CELL_TESTID_PREFIX,
} from '@ooh/screens';
import { useMultiSelectAnchor, useSelectBatchResults } from '@ooh/screens';
import type { UnifiedSelectCellResolution } from '@ooh/screens';
import type { buildOfferUnitEntriesGrid } from '../lib/offer-availability-mapping';
import type { OfferUnitItem } from '../units-tab-columns';
import { useMultiSelectClick } from './use-multi-select-click';
import { useMultiSelectDrag } from './use-multi-select-drag';
import { useMultiSelectCellResolution } from './use-multi-select-cell-resolution';

export interface UseOfferTableMultiSelectOptions {
  /** Whether the caller may start a new selection (`canEdit && reservationActions.canCreateReservation`). */
  canSelect: boolean;
  rows: OfferUnitItem[];
  periods: InventoryAvailabilityPeriod[];
  entriesGrid: ReturnType<typeof buildOfferUnitEntriesGrid>;
  gridRows: { id: string; label: string }[];
  grid: AvailabilityGridData;
  mediaUnitIdById: Map<string, number>;
  refetchAvailability: () => void;
}

export interface UseOfferTableMultiSelectResult {
  multiSelection: MultiCellSelection | null;
  multiSelectAnchorRef: React.RefObject<HTMLElement | null>;
  clearMultiSelection: () => void;
  resolveMultiSelectCell: (ref: MultiSelectCellRef) => UnifiedSelectCellResolution | null;
  handleCellClick: (rowId: string, period: InventoryAvailabilityPeriod, shiftKey: boolean) => void;
  handleCellMouseDown: (rowId: string, period: InventoryAvailabilityPeriod) => void;
  handleCellMouseEnter: (rowId: string, period: InventoryAvailabilityPeriod) => void;
  handleCellMouseUp: (rowId: string, period: InventoryAvailabilityPeriod) => void;
  handleBatchReserveSuccess: (response: BatchReserveResponse) => void;
  handleBatchReleaseSuccess: (response: BatchReleaseResponse) => void;
}

function useOfferRowIdOfMediaUnit(rows: OfferUnitItem[]) {
  return React.useCallback(
    (mediaUnitId: number) => {
      const row = rows.find((r) => r.mediaUnitId === mediaUnitId);
      return row ? String(row.id) : undefined;
    },
    [rows],
  );
}

export function useOfferTableMultiSelect({
  canSelect,
  rows,
  periods,
  entriesGrid,
  gridRows,
  grid,
  mediaUnitIdById,
  refetchAvailability,
}: UseOfferTableMultiSelectOptions): UseOfferTableMultiSelectResult {
  // Prefix MUST match the actual rendered cell testid: the offer table's
  // availability columns render via `RichAvailabilityCell` (the same
  // component the catalog uses), which always emits
  // `RICH_AVAILABILITY_CELL_TESTID_PREFIX-{unitId}-{periodId}` regardless of
  // caller — never an `offer-table-*`-prefixed id. The old mismatched
  // literal prefix here meant `resolveMultiSelectAnchorElement` could never
  // find the anchor cell, so `UnifiedSelectPopover` fell back to its
  // un-positioned default instead of tracking the dragged/released cell
  // (CU-86cbejhgd). Importing the shared constant instead of retyping the
  // literal means a future rename of the cell's testid can't silently
  // reintroduce this class of bug.
  const {
    multiSelection,
    anchorRef: multiSelectAnchorRef,
    updateSelection: updateMultiSelectAnchor,
    narrowSelection: narrowMultiSelection,
  } = useMultiSelectAnchor(RICH_AVAILABILITY_CELL_TESTID_PREFIX);

  const clearMultiSelection = React.useCallback(
    () => updateMultiSelectAnchor(null),
    [updateMultiSelectAnchor],
  );

  const resolveMultiSelectCell = useMultiSelectCellResolution({
    periods,
    entriesGrid,
    mediaUnitIdById,
  });

  const geometry = { canSelect, gridRows, periods, grid, updateMultiSelectAnchor };
  const handleCellClick = useMultiSelectClick({ ...geometry, multiSelection });
  const { handleCellMouseDown, handleCellMouseEnter, handleCellMouseUp } =
    useMultiSelectDrag(geometry);
  const unitIdOf = useOfferRowIdOfMediaUnit(rows);
  const { handleBatchReserveSuccess, handleBatchReleaseSuccess } = useSelectBatchResults({
    periods,
    narrowSelection: narrowMultiSelection,
    resolveCell: resolveMultiSelectCell,
    unitIdOf,
    refreshAvailability: refetchAvailability,
  });

  return {
    multiSelection,
    multiSelectAnchorRef,
    clearMultiSelection,
    resolveMultiSelectCell,
    handleCellClick,
    handleCellMouseDown,
    handleCellMouseEnter,
    handleCellMouseUp,
    handleBatchReserveSuccess,
    handleBatchReleaseSuccess,
  };
}
